import { useCheckbox } from '@react-aria/checkbox';
import { useFocusRing } from '@react-aria/focus';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useToggleState } from '@react-stately/toggle';
import type { ToggleProps } from '@react-types/checkbox';
import { FocusEventHandler, MouseEventHandler, useCallback, useRef } from 'react';

import { CheckSimple as CheckSimpleIcon } from '@calm-web/icons';

import { AccessibleOnChange, useAccessibleOnChange } from '../../utils/useAccessibleOnChange';
import { CheckboxBox, Container, ErrorMessage, LabelWrapper } from './styles';
import { CheckboxAlign, CheckboxProps, CheckboxVerticalAlign } from './types';

const defaultOnChange: AccessibleOnChange<HTMLInputElement> = () => {};

const Checkbox = ({
	alignment = CheckboxAlign.Left,
	checked,
	onFocus: propsOnFocus,
	onBlur: propsOnBlur,
	onChange = defaultOnChange,
	textSize,
	className,
	disabled,
	verticalAlignment = CheckboxVerticalAlign.Center,
	isStrong,
	isValid = true,
	showValidation = true,
	errors,
	isHorizontalError = false,
	...props
}: CheckboxProps): React.ReactElement => {
	const ref = useRef<HTMLInputElement>(null);
	const accessibleOnChange = useAccessibleOnChange(onChange, props.name, 'checkbox', ref);
	// TODO: figure out how to not have to do this casting
	const accessibleOnFocus = propsOnFocus as unknown as ToggleProps['onFocus'];
	const accessibleOnBlur = propsOnBlur as unknown as ToggleProps['onBlur'];
	const reactAriaProps = {
		...props,
		isSelected: checked,
		onChange: accessibleOnChange,
		onFocus: accessibleOnFocus,
		onBlur: accessibleOnBlur,
		onClick: props.onClick as MouseEventHandler,
	};
	const state = useToggleState(reactAriaProps);
	const { inputProps } = useCheckbox(reactAriaProps, state, ref);
	const { isFocusVisible, focusProps } = useFocusRing();

	const { onFocus: focusPropsOnFocus, onBlur: focusPropsOnBlur } = focusProps;
	const { onFocus: inputPropsOnFocus, onBlur: inputPropsOnBlur } = inputProps;
	const onFocus: FocusEventHandler<HTMLInputElement> = useCallback(
		e => {
			focusPropsOnFocus?.(e);
			inputPropsOnFocus?.(e);
		},
		[focusPropsOnFocus, inputPropsOnFocus],
	);
	const onBlur: FocusEventHandler<HTMLInputElement> = useCallback(
		e => {
			focusPropsOnBlur?.(e);
			inputPropsOnBlur?.(e);
		},
		[focusPropsOnBlur, inputPropsOnBlur],
	);
	const inputAndFocusProps = disabled ? { tabIndex: -1 } : { ...inputProps, ...focusProps, onFocus, onBlur };

	return (
		<LabelWrapper
			$alignment={alignment}
			$isSelected={state.isSelected}
			$textSize={textSize}
			$disabled={disabled}
			$verticalAlignment={verticalAlignment}
			className={className}
			onClick={props.onClick}
		>
			<VisuallyHidden>
				<input {...inputAndFocusProps} ref={ref} />
			</VisuallyHidden>
			<CheckboxBox
				$isSelected={state.isSelected}
				$isFocusVisible={isFocusVisible}
				$isStrong={isStrong}
				$isValid={!showValidation || isValid}
			>
				{state.isSelected && <CheckSimpleIcon />}
			</CheckboxBox>
			{showValidation && !isValid && errors && errors.length > 0 ? (
				<Container $isHorizontalError={isHorizontalError}>
					{props.children}
					<ErrorMessage isHorizontalError={isHorizontalError}>{errors[0]}</ErrorMessage>
				</Container>
			) : (
				props.children
			)}
		</LabelWrapper>
	);
};

export default Checkbox;
